import { FormEvent, Fragment } from "react"
import { Head, useForm, usePage } from "@inertiajs/react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { ProfileSubNav } from "./ProfileSubNav"

import type { PageProps } from "@/types/inertia"

type Contributions = {
  bands: number; people: number; recordings: number; gigs: number
  venues: number; images: number; videos: number; total: number
}

type QuizStats = {
  total_answered: number; total_correct: number; percentage: number
}

export default function ProfileDetails({ contributions, quiz_stats }: { contributions: Contributions; quiz_stats: QuizStats }) {
  const { props } = usePage<PageProps>()
  const user = props.current_user
  const errors = props.errors ?? {}

  const emailForm       = useForm({ email: user?.email ?? "" })
  const wofForm         = useForm({ show_on_wall_of_fame: String(user?.show_on_wall_of_fame ?? true) })
  const leaderboardForm = useForm({ show_on_leaderboard: String(user?.show_on_leaderboard ?? true) })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    emailForm.patch("/profile/email", { preserveScroll: true })
  }

  const toggleWof = (checked: boolean) => {
    wofForm.setData("show_on_wall_of_fame", String(checked))
    wofForm.patch("/profile/wall-of-fame", { preserveScroll: true })
  }

  const toggleLeaderboard = (checked: boolean) => {
    leaderboardForm.setData("show_on_leaderboard", String(checked))
    leaderboardForm.patch("/profile/leaderboard", { preserveScroll: true })
  }

  return (
    <>
      <Head title="My details">
        <meta name="description" content="Update the email address you use to log in." />
        <meta property="og:title" content="My details" />
        <meta property="og:description" content="Update the email address you use to log in." />
      </Head>
      <AppShell>
        <PageHeader
          title="Profile"
          description="Manage your account."
          tabs={<ProfileSubNav active="details" />}
        />

        {props.flash?.notice && (
          <p className="mt-6 text-sm text-accent">{props.flash.notice}</p>
        )}

        <section className="mt-10 max-w-md space-y-8">
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="email">Email</label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                aria-invalid={!!errors.email}
                value={emailForm.data.email}
                onChange={(e) => emailForm.setData("email", e.target.value)}
              />
              {errors.email && (
                <p className="text-xs text-danger-display">{errors.email}</p>
              )}
            </div>
            <Button type="submit" disabled={emailForm.processing}>
              Update email
            </Button>
          </form>

          <div className="border-t border-hairline pt-8">
            <h2 className="text-base mb-1">Wall of Fame</h2>
            <p className="text-sm text-ink-muted mb-4">
              If you've contributed records to the database, you can choose whether your name appears in the contributors section of the{" "}
              <a href="/wall-of-fame">Wall of Fame</a>.
            </p>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4 accent-amber-500"
                checked={wofForm.data.show_on_wall_of_fame === "true"}
                disabled={wofForm.processing}
                onChange={(e) => toggleWof(e.target.checked)}
              />
              <span className="text-sm">Show my name on the Wall of Fame</span>
            </label>
          </div>

          <div className="border-t border-hairline pt-8">
            <h2 className="text-base mb-1">Trivia Leaderboard</h2>
            <p className="text-sm text-ink-muted mb-4">
              Choose whether your score appears on the public{" "}
              <a href="/leaderboard">trivia leaderboard</a>.
            </p>
            {quiz_stats.total_answered > 0 ? (
              <p className="text-sm text-ink-body mb-4">
                You've answered <strong>{quiz_stats.total_answered}</strong>{" "}
                question{quiz_stats.total_answered === 1 ? "" : "s"}, with{" "}
                <strong>{quiz_stats.total_correct}</strong> correct ({quiz_stats.percentage}%).
              </p>
            ) : (
              <p className="text-sm text-ink-muted mb-4">
                You haven't answered any <a href="/trivia">trivia questions</a> yet.
              </p>
            )}
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4 accent-amber-500"
                checked={leaderboardForm.data.show_on_leaderboard === "true"}
                disabled={leaderboardForm.processing}
                onChange={(e) => toggleLeaderboard(e.target.checked)}
              />
              <span className="text-sm">Show my score on the leaderboard</span>
            </label>
          </div>

          <div className="border-t border-hairline pt-8">
            <h2 className="text-base mb-1">My contributions</h2>
            <p className="text-sm text-ink-muted mb-4">Records you've added to the database.</p>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-[max-content_1fr]">
              {([
                ["Bands",      contributions.bands],
                ["People",     contributions.people],
                ["Recordings", contributions.recordings],
                ["Gigs",       contributions.gigs],
                ["Venues",     contributions.venues],
                ["Images",     contributions.images],
                ["Videos",     contributions.videos],
              ] as [string, number][]).map(([label, count]) => (
                <Fragment key={label}>
                  <dt className="text-sm text-ink-muted">{label}</dt>
                  <dd className="text-sm text-ink-body">{count.toLocaleString()}</dd>
                </Fragment>
              ))}
              <dt className="text-sm text-ink-muted font-medium">Total</dt>
              <dd className="text-sm text-ink-body font-medium">{contributions.total.toLocaleString()}</dd>
            </dl>
          </div>
        </section>
      </AppShell>
    </>
  )
}
